import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { adsApi } from '@/api/client';
import { extractAssociatedCampaigns } from '@/utils/budgetRuleCampaigns';
import type { CampaignAdProduct } from '@/types/spCampaigns';
import { campaignAdProductToBudgetRuleApiProduct } from '@/types/spCampaigns';

export function budgetRuleCampaignsQueryKey(
  profileId: string,
  product: CampaignAdProduct,
  ruleId: string
) {
  const apiProduct = campaignAdProductToBudgetRuleApiProduct(product);
  return ['budget-rule-campaigns', apiProduct, profileId, ruleId] as const;
}

export function useBudgetRuleAssociatedCampaigns(
  profileId: string,
  product: CampaignAdProduct,
  ruleId: string,
  enabled = true
) {
  const apiProduct = campaignAdProductToBudgetRuleApiProduct(product);
  const queryKey = budgetRuleCampaignsQueryKey(profileId, product, ruleId);

  const query = useQuery({
    queryKey,
    queryFn: async () => {
      const res = await adsApi.getBudgetRuleCampaigns(profileId, apiProduct, ruleId);
      if (!res.success) throw new Error('Failed to load linked campaigns');
      return res.data;
    },
    enabled: enabled && !!profileId && !!ruleId,
    staleTime: 60_000,
  });

  const associated = useMemo(
    () => extractAssociatedCampaigns(query.data),
    [query.data]
  );

  return { ...query, associated, queryKey };
}
